/* eslint-disable no-console -- P0 uses console.error directly; we'll route through the logger when persistence lands. */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { BoardWidget, View } from '@amzn/ad-console-canvas';
import type { RootState } from '@urc-ui/store/types';
import {
  MAX_WIDGETS_TOTAL,
  PANTHEON_SOURCE_NAME,
  VIZ_TYPE_TO_CATEGORY,
  VIZ_TYPE_TO_PANTHEON_MODULE_ID,
  WidgetCategory
} from '@flexible-reports/pages/analysis-workspace/constants';
import { initialAnalysisWorkspaceView } from '@flexible-reports/pages/analysis-workspace/initial-view-data';
import { recomputeFullWidthLayout, recomputeLayout } from '@flexible-reports/pages/analysis-workspace/placement';
import type { AddWidgetEnvelope, RemoveWidgetEnvelope, UpdateWidgetEnvelope } from '@flexible-reports/pages/analysis-workspace/types';

export interface AnalysisWorkspaceState {
  view: View | null;
  expandedWidgetIds: string[];
  highlightedWidgetIds: string[];
  /**
   * Name of the report this analysis was initiated from, used to render the
   * "Analysis of {reportName}" header. `null` for chat-initiated sessions, which
   * fall back to the generic "Analysis workspace" title.
   */
  reportName: string | null;
}

const buildInitialState = (): AnalysisWorkspaceState => ({
  view: initialAnalysisWorkspaceView(),
  expandedWidgetIds: [],
  highlightedWidgetIds: [],
  reportName: null
});

/**
 * Resolves which `visualizationSpec.type` (or the literal `'markdown'`) the envelope routes on,
 * the layout category that determines sizing, and the Pantheon module that renders it.
 *
 * Returns `null` and logs when the envelope is missing fields or names an
 * unsupported type. Centralized here so add/update share the validation.
 */
const resolveEnvelopeRouting = (
  envelope: AddWidgetEnvelope,
  context: 'addWidget' | 'updateWidget'
): { category: WidgetCategory; moduleId: string } | null => {
  const typeKey = envelope.contentType === 'markdown' ? 'markdown' : envelope.visualizationSpec?.type;
  if (!typeKey) {
    console.error(`analysisWorkspace.${context}: missing visualizationSpec.type`, envelope);
    return null;
  }

  const category = VIZ_TYPE_TO_CATEGORY[typeKey];
  const moduleId = VIZ_TYPE_TO_PANTHEON_MODULE_ID[typeKey];
  if (!category || !moduleId) {
    console.error(`analysisWorkspace.${context}: unsupported visualizationSpec.type "${typeKey}"`);
    return null;
  }

  return { category, moduleId };
};

/** Finds a widget by its widgetId, or -1 if not present. */
const findWidgetIndex = (view: View, agentWidgetId: string): number => view.widgets.findIndex(w => w.widgetId === agentWidgetId);

/** Widgets that accept pre-transformed props directly (no envelope wrapper). */
const DIRECT_PROPS_MODULES = new Set([
  'canvas-widget-bubble-chart',
  'canvas-widget-heat-map',
  'canvas-widget-scatter-chart',
  'canvas-widget-pyramid-chart'
]);

const buildBoardWidget = (envelope: AddWidgetEnvelope, moduleId: string, category: WidgetCategory): BoardWidget => ({
  type: `${PANTHEON_SOURCE_NAME}:${moduleId}`,
  widgetId: envelope.widgetId,
  props:
    DIRECT_PROPS_MODULES.has(moduleId) && envelope.contentType === 'chart' && envelope.visualizationSpec
      ? { id: envelope.widgetId, category, ...envelope.visualizationSpec }
      : { envelope, category }
});

const ensureCanvas = (state: AnalysisWorkspaceState): View => {
  if (!state.view) state.view = initialAnalysisWorkspaceView();
  return state.view;
};

/** Recomputes the canonical layout from the current widget list and writes it back. */
const syncLayout = (view: View, expandedWidgetIds: string[] = []): void => {
  const expandedSet = expandedWidgetIds.length > 0 ? new Set(expandedWidgetIds) : undefined;
  view.layout.lg = recomputeLayout(view.widgets, expandedSet);
  view.layout.md = recomputeFullWidthLayout(view.widgets, 6);
  view.layout.sm = recomputeFullWidthLayout(view.widgets, 4);
};

const analysisWorkspaceSlice = createSlice({
  name: 'analysisWorkspace',
  initialState: buildInitialState(),
  reducers: {
    addWidget: (state, action: PayloadAction<AddWidgetEnvelope>) => {
      const envelope = action.payload;
      if (!envelope?.widgetId) {
        console.error('analysisWorkspace.addWidget: missing widgetId', envelope);
        return;
      }

      const routing = resolveEnvelopeRouting(envelope, 'addWidget');
      if (!routing) return;

      const view = ensureCanvas(state);

      if (view.widgets.length >= MAX_WIDGETS_TOTAL) {
        console.error(`analysisWorkspace.addWidget: canvas at MAX_WIDGETS_TOTAL (${MAX_WIDGETS_TOTAL})`);
        return;
      }

      if (findWidgetIndex(view, envelope.widgetId) !== -1) {
        console.error(`analysisWorkspace.addWidget: widgetId "${envelope.widgetId}" already exists`);
        return;
      }

      view.widgets.push(buildBoardWidget(envelope, routing.moduleId, routing.category));
      syncLayout(view, state.expandedWidgetIds);
      state.highlightedWidgetIds.push(envelope.widgetId);
    },

    updateWidget: (state, action: PayloadAction<UpdateWidgetEnvelope>) => {
      const envelope = action.payload;
      if (!envelope?.widgetId) {
        console.error('analysisWorkspace.updateWidget: missing widgetId', envelope);
        return;
      }
      if (!state.view) {
        console.error(`analysisWorkspace.updateWidget: no canvas yet (widgetId "${envelope.widgetId}")`);
        return;
      }

      const view = state.view;
      const widgetIndex = findWidgetIndex(view, envelope.widgetId);
      if (widgetIndex === -1) {
        console.error(`analysisWorkspace.updateWidget: widgetId "${envelope.widgetId}" not found`);
        return;
      }

      const routing = resolveEnvelopeRouting(envelope, 'updateWidget');
      if (!routing) return;

      view.widgets[widgetIndex] = buildBoardWidget(envelope, routing.moduleId, routing.category);
      syncLayout(view, state.expandedWidgetIds);
    },

    removeWidget: (state, action: PayloadAction<RemoveWidgetEnvelope>) => {
      const widgetId = action.payload?.widgetId;
      if (!widgetId) {
        console.error('analysisWorkspace.removeWidget: missing widgetId');
        return;
      }
      if (!state.view) {
        console.error(`analysisWorkspace.removeWidget: no canvas yet (widgetId "${widgetId}")`);
        return;
      }

      const view = state.view;
      const widgetIndex = findWidgetIndex(view, widgetId);
      if (widgetIndex === -1) {
        console.error(`analysisWorkspace.removeWidget: widgetId "${widgetId}" not found`);
        return;
      }

      view.widgets.splice(widgetIndex, 1);
      state.expandedWidgetIds = state.expandedWidgetIds.filter(id => id !== widgetId);
      state.highlightedWidgetIds = state.highlightedWidgetIds.filter(id => id !== widgetId);
      syncLayout(view, state.expandedWidgetIds);
    },

    resetCanvas: state => {
      state.view = initialAnalysisWorkspaceView();
      state.expandedWidgetIds = [];
      state.highlightedWidgetIds = [];
    },

    addBoardWidget: (
      state,
      action: PayloadAction<{ moduleId: string; agentWidgetId: string; props: Record<string, unknown>; category: WidgetCategory }>
    ) => {
      const { moduleId, agentWidgetId, props, category } = action.payload;
      const view = ensureCanvas(state);

      if (view.widgets.length >= MAX_WIDGETS_TOTAL) {
        console.error(`analysisWorkspace.addBoardWidget: canvas at MAX_WIDGETS_TOTAL (${MAX_WIDGETS_TOTAL})`);
        return;
      }

      if (view.widgets.some(w => w.widgetId === agentWidgetId)) {
        console.error(`analysisWorkspace.addBoardWidget: widgetId "${agentWidgetId}" already exists`);
        return;
      }

      view.widgets.push({ type: `${PANTHEON_SOURCE_NAME}:${moduleId}`, widgetId: agentWidgetId, props: { ...props, category } });
      syncLayout(view, state.expandedWidgetIds);
      state.highlightedWidgetIds.push(agentWidgetId);
    },

    clearHighlight: (state, action: PayloadAction<string>) => {
      state.highlightedWidgetIds = state.highlightedWidgetIds.filter(id => id !== action.payload);
    },

    setAnalysisReportName: (state, action: PayloadAction<string>) => {
      state.reportName = action.payload;
    },

    clearAnalysisReportName: state => {
      state.reportName = null;
    },

    toggleWidgetExpand: (state, action: PayloadAction<{ widgetId: string; expanded: boolean }>) => {
      const { widgetId, expanded } = action.payload;
      if (!state.view) return;
      if (expanded) {
        if (!state.expandedWidgetIds.includes(widgetId)) state.expandedWidgetIds.push(widgetId);
      } else {
        state.expandedWidgetIds = state.expandedWidgetIds.filter(id => id !== widgetId);
      }
      syncLayout(state.view, state.expandedWidgetIds);
    }
  }
});

export const {
  addWidget,
  updateWidget,
  removeWidget,
  resetCanvas,
  addBoardWidget,
  toggleWidgetExpand,
  clearHighlight,
  setAnalysisReportName,
  clearAnalysisReportName
} = analysisWorkspaceSlice.actions;

export const selectAnalysisWorkspaceView = (state: RootState): View | null => state.analysisWorkspace.view;

export const selectAnalysisReportName = (state: RootState): string | null => state.analysisWorkspace.reportName;

export const selectIsWidgetExpanded =
  (widgetId: string) =>
  (state: RootState): boolean =>
    state.analysisWorkspace.expandedWidgetIds.includes(widgetId);

export const selectHighlightedWidgetIds = (state: RootState): string[] => state.analysisWorkspace.highlightedWidgetIds;

export const selectIsWidgetHighlighted =
  (widgetId: string) =>
  (state: RootState): boolean =>
    state.analysisWorkspace.highlightedWidgetIds.includes(widgetId);

export default analysisWorkspaceSlice.reducer;
